/**
 * PageSkeleton — branded route-transition loading screen (#475).
 *
 * Rendered as the Suspense fallback during heavy route transitions and
 * also as the in-content skeleton when React Query data is loading.
 * Preserves the sidebar + header chrome so the screen never goes fully blank.
 *
 * Also used as the ProtectedRoute admin-shell loading/branch-level Suspense
 * fallback (#676) — this is a plain, eagerly-imported component (no lazy
 * chunk, no data-fetching hooks), so it paints immediately on first render
 * instead of waiting on a chunk download the way the previous AdminLayout
 * skeleton did.
 *
 * Design principles:
 *  - Mimic the real layout chrome (sidebar stripe, header bar).
 *  - Content area shows a neutral block skeleton — no fake UI shapes.
 *  - Respects prefers-reduced-motion (Skeleton component already handles this).
 *  - Screen readers see an appropriate status region (role=status + aria-live
 *    + aria-busy), announced via the shared, already-translated `common:loading`
 *    key rather than a hardcoded English literal.
 */

import { useTranslation } from '@/hooks/useTranslation';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface PageSkeletonProps {
  className?: string;
}

export function PageSkeleton({ className }: PageSkeletonProps) {
  const { t } = useTranslation('common');

  return (
    <div
      className={cn('min-h-screen bg-bg flex', className)}
      role="status"
      aria-busy="true"
      aria-label={t('loading')}
      aria-live="polite"
    >
      {/* Sidebar chrome stand-in — solid stripe matching sidebar width */}
      <div
        className="hidden lg:block w-64 shrink-0 border-r bg-surface"
        aria-hidden="true"
      >
        {/* Brand area */}
        <div className="h-[60px] border-b border-border px-5 flex items-center">
          <Skeleton className="h-5 w-28 rounded-md" />
        </div>
        {/* ⌘K bar */}
        <div className="px-3.5 py-3">
          <Skeleton className="h-8 w-full rounded-[10px]" />
        </div>
        {/* Nav skeletons */}
        <div className="px-4 space-y-1.5 pt-1">
          {[60, 45, 70, 55, 65, 40, 58].map((w, i) => (
            <div key={i} className="flex items-center gap-2.5 py-[7px]">
              <Skeleton className="h-4 w-4 shrink-0 rounded" />
              <Skeleton className="h-3 rounded" style={{ width: `${w}%` }} />
            </div>
          ))}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col">
        {/* Header chrome stand-in */}
        <div
          className="sticky top-0 z-30 h-[72px] border-b bg-bg/95 flex items-center px-7 gap-4"
          aria-hidden="true"
        >
          {/* Mobile hamburger placeholder */}
          <div className="w-10 lg:hidden" />
          {/* Title */}
          <div className="flex-1">
            <Skeleton className="h-7 w-36 rounded" />
            <Skeleton className="h-3 w-48 rounded mt-1.5" />
          </div>
          {/* Right cluster */}
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-32 rounded-lg" />
            <Skeleton className="h-8 w-8 rounded-lg" />
            <Skeleton className="h-8 w-8 rounded-lg" />
          </div>
        </div>

        {/* Content skeleton */}
        <main className="container mx-auto p-6" aria-hidden="true">
          {/* KPI strip — mirrors Dashboard.tsx's responsive breakpoints (RESP-003)
              so the skeleton doesn't shift layout when real content mounts. */}
          <div className="grid gap-[18px] mb-[18px] grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-[138px] rounded-card" />
            ))}
          </div>
          {/* Row 1 — mirrors Dashboard.tsx row 1 (RESP-004) */}
          <div className="grid gap-[18px] mb-[18px] grid-cols-1 lg:grid-cols-[1.7fr_1fr]">
            <Skeleton className="h-[220px] rounded-card" />
            <div className="flex flex-col gap-[18px]">
              <Skeleton className="h-[100px] rounded-card" />
              <Skeleton className="flex-1 rounded-card" />
            </div>
          </div>
          {/* Row 2 — mirrors Dashboard.tsx row 2 (RESP-004) */}
          <div className="grid gap-[18px] grid-cols-1 lg:grid-cols-[1.7fr_1fr]">
            <Skeleton className="h-[200px] rounded-card" />
            <Skeleton className="h-[200px] rounded-card" />
          </div>
        </main>
      </div>
    </div>
  );
}
